import { useEffect } from "react";
import { useMap } from "@vis.gl/react-google-maps";

// 開発環境のみでデバッグを有効化
// REACT_APP_ENVが未設定または空の場合は開発環境とみなす
// prod/stagingではREACT_APP_ENVが設定される
const DEBUG = !process.env.REACT_APP_ENV || process.env.REACT_APP_ENV === '';

export default function useMapCentering(selectedMarker) {
    const map = useMap();

    useEffect(() => {
        if (!map || !selectedMarker?.position) return;

        // 地図の現在の状態を取得
        const zoom = map.getZoom?.();
        const bearing = map.getHeading?.() || 0;
        const tilt = map.getTilt?.() || 0;

        if (typeof zoom !== 'number') return;

        // デバイスサイズに基づくオフセット設定（breakpoints.cssに合わせて調整）
        const isMobile = window.innerWidth <= 440;
        const isTablet = window.innerWidth < 1400;

        const OFFSET_MOBILE = -175;
        const OFFSET_TABLET = -200;
        const OFFSET_DESKTOP = -200;

        let pixelOffsetY;
        if (isMobile) {
            pixelOffsetY = OFFSET_MOBILE;
        } else if (isTablet) {
            pixelOffsetY = OFFSET_TABLET;
        } else {
            pixelOffsetY = OFFSET_DESKTOP;
        }

        // 改善: 開発環境のみでデバッグ情報を出力
        if (DEBUG) {
            console.log('Map centering debug:', {
                bearing,
                tilt,
                zoom,
                pixelOffsetY,
                isMobile,
                isTablet,
                windowWidth: window.innerWidth
            });
        }

        // 3D地図操作時は、現在の視点を維持したままセンタリング
        if (bearing !== 0 || tilt !== 0) {
            if (DEBUG) {
                console.log('Using 3D centering method');
            }

            // 回転とチルトを考慮したオフセット計算
            const bearingRad = bearing * Math.PI / 180; // 回転角度をラジアンに変換

            // チルトを考慮したオフセット調整（チルトが大きいほどオフセットを大きくする）
            const tiltFactor = 1 + (tilt / 90); // チルト0度で1、90度で2
            const adjustedOffset = pixelOffsetY * tiltFactor;
            const latOffset = adjustedOffset / (1000 * Math.pow(2, zoom - 10));

            // 回転を考慮したオフセット計算
            const offsetX = Math.sin(bearingRad) * latOffset;
            const offsetY = Math.cos(bearingRad) * latOffset;

            const newCenterLat = selectedMarker.position.lat - offsetY;
            const newCenterLng = selectedMarker.position.lng - offsetX;

            if (DEBUG) {
                console.log('3D centering:', {
                    tiltFactor,
                    bearingRad,
                    adjustedOffset,
                    latOffset,
                    offsetX,
                    offsetY,
                    newCenterLat,
                    newCenterLng
                });
            }

            // 現在の視点（bearing, tilt, zoom）を維持したまま中心点を移動
            map.panTo({ lat: newCenterLat, lng: newCenterLng });
        } else {
            if (DEBUG) {
                console.log('Using 2D centering method');
            }
            // 2D地図の場合は従来の投影座標系を使用
            const proj = map.getProjection?.();
            if (!proj) return;

            const scale = Math.pow(2, zoom);
            const worldCoordinate = proj.fromLatLngToPoint(selectedMarker.position);

            if (!worldCoordinate) return;

            const newWorldPoint = {
                x: worldCoordinate.x,
                y: worldCoordinate.y + (pixelOffsetY / scale)
            };

            const newCenterLatLng = proj.fromPointToLatLng(newWorldPoint);

            if (newCenterLatLng) {
                map.panTo(newCenterLatLng);
            }
        }
    }, [map, selectedMarker]);
}